import * as React from "react"
import PropTypes from "prop-types"
import { DataGridPro, GridActionsCellItem } from "@mui/x-data-grid-pro"
import { Stack, Button, Typography } from "@mui/material"
import { Delete } from "@mui/icons-material"
import { useDecimalFormatter } from "hooks/useDecimalFormatter"
import { DIMENSIONS, MEASUREMENTS } from "constants/decimals"
import i18n from "i18n"

function NoRowsOverlay() {
  return (
    <Stack
      sx={{ width: "100%", height: "100%" }}
      alignItems="center"
      justifyContent="center"
      direction="row"
    >
      <Typography
        color="text.secondary"
        variant="body2"
        sx={{ fontSize: "0.85rem" }}
      >
        {i18n.t("measurements.noRows")}
      </Typography>
    </Stack>
  )
}

const getColumns = (onDeleteRow, roundToDecimals, isReadOnly) => [
  {
    field: "description",
    headerName: i18n.t("measurements.description"),
    flex: 2,
    editable: !isReadOnly,
  },
  {
    field: "quantity",
    headerName: i18n.t("measurements.quantity"),
    type: "number",
    flex: 1,
    editable: !isReadOnly,
    align: "center",
    headerAlign: "center",
    renderCell: params =>
      params.value == null ? "" : roundToDecimals(params.value, DIMENSIONS),
  },
  {
    field: "depth",
    headerName: i18n.t("measurements.depth"),
    type: "number",
    flex: 1,
    editable: !isReadOnly,
    align: "center",
    headerAlign: "center",
    renderCell: params =>
      params.value == null ? "" : roundToDecimals(params.value, DIMENSIONS),
  },
  {
    field: "width",
    headerName: i18n.t("measurements.width"),
    type: "number",
    flex: 1,
    editable: !isReadOnly,
    align: "center",
    headerAlign: "center",
    renderCell: params =>
      params.value == null ? "" : roundToDecimals(params.value, DIMENSIONS),
  },
  {
    field: "height",
    headerName: i18n.t("measurements.height"),
    type: "number",
    flex: 1,
    editable: !isReadOnly,
    align: "center",
    headerAlign: "center",
    renderCell: params =>
      params.value == null ? "" : roundToDecimals(params.value, DIMENSIONS),
  },
  {
    field: "total",
    headerName: i18n.t("measurements.total"),
    type: "number",
    flex: 1,
    editable: false,
    align: "center",
    headerAlign: "center",
    renderCell: params =>
      params.value == null ? "" : roundToDecimals(params.value, MEASUREMENTS),
  },
  {
    field: "actions",
    headerName: "",
    width: 40,
    type: "actions",
    getActions: params =>
      [
        !isReadOnly && (
          <GridActionsCellItem
            key="delete"
            icon={<Delete />}
            label={i18n.t("measurements.delete")}
            onClick={() => onDeleteRow(params.id)}
            className="delete-action-button"
          />
        ),
      ].filter(Boolean),
    sortable: false,
    filterable: false,
    disableExport: true,
    disableColumnMenu: true,
    disableColumnSelector: true,
    hideable: false,
    pinned: "right",
  },
]

function getTotal(row) {
  const isNullOrZero = v => v == null || v === undefined || v === 0

  const q = isNullOrZero(row.quantity) ? null : Number(row.quantity)
  const d = isNullOrZero(row.depth) ? null : Number(row.depth)
  const w = isNullOrZero(row.width) ? null : Number(row.width)
  const h = isNullOrZero(row.height) ? null : Number(row.height)
  // Si todos son null o 0, el total es 0
  if ([q, d, w, h].every(v => v === null)) return 0
  // Los null se toman como 1
  const total = (q ?? 1) * (d ?? 1) * (w ?? 1) * (h ?? 1)

  console.log("Calculating total for row:", row, "=>", total)
  return total
}

const MedicionesDataGrid = ({ mediciones, setMediciones, isReadOnly }) => {
  const { roundToDecimals } = useDecimalFormatter()
  const rows = mediciones.map((m, i) => ({ id: i, ...m }))

  const handleDeleteRow = rowId => {
    setMediciones(prev => prev.filter((_, idx) => idx !== rowId))
  }

  const handleProcessRowUpdate = (newRow, oldRow) => {
    const updated = [...mediciones]
    updated[newRow.id] = { ...newRow, total: getTotal(newRow) }
    setMediciones(updated)
    return { ...newRow, total: getTotal(newRow) }
  }

  const handleAddRow = () => {
    setMediciones([
      ...mediciones,
      {
        description: "",
        quantity: null,
        depth: null,
        width: null,
        height: null,
        total: 0,
      },
    ])
  }

  return (
    <div style={{ width: "100%" }}>
      <div style={{ height: "150px", width: "100%" }}>
        <DataGridPro
          rows={rows}
          columns={getColumns(handleDeleteRow, roundToDecimals, isReadOnly)}
          processRowUpdate={!isReadOnly ? handleProcessRowUpdate : undefined}
          experimentalFeatures={{ newEditingApi: true }}
          disableSelectionOnClick
          hideFooter
          density="compact"
          initialState={{
            pinnedColumns: {
              right: ["actions"],
            },
          }}
          disableColumnMenu
          disableColumnFilter
          disableColumnSelector
          disableColumnSort
          sx={{
            "& .MuiDataGrid-row": {
              "&:hover .delete-action-button": {
                opacity: 1,
              },
              "& .delete-action-button": {
                opacity: 0,
                transition: "opacity 0.2s ease-in-out",
              },
            },
          }}
          slots={{ noRowsOverlay: NoRowsOverlay }}
        />
      </div>
      {!isReadOnly && (
        <Button
          size="small"
          onClick={handleAddRow}
          style={{ marginTop: "10px", width: "fit-content" }}
        >
          {i18n.t("measurements.newMeasurement")}
        </Button>
      )}
    </div>
  )
}

MedicionesDataGrid.propTypes = {
  mediciones: PropTypes.array.isRequired,
  setMediciones: PropTypes.func.isRequired,
  isReadOnly: PropTypes.bool,
}

export default MedicionesDataGrid
